// @ts-nocheck
// =============================================================================
//  KitchenControl — Team Module
//  Props: { sb, tenantId, activeTab, onTabChange }
//  Gedeelde medewerkers store — ook gebruikt door HACCP
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import { Users, X, Download, ChevronLeft, ChevronRight } from "lucide-react";

// ── Kleuren ───────────────────────────────────────────────────────────────────
const C = {
  bg:      "#F8FAFC",
  bg2:     "#FFFFFF",
  bg3:     "#F1F5F9",
  hdr:     "#0F172A",
  hdrBdr:  "#1E293B",
  txt:     "#0F172A",
  txt2:    "#475569",
  txt3:    "#94A3B8",
  bdr:     "#E2E8F0",
  bdr2:    "#CBD5E1",
  pri:     "#2563EB",
  priBg:   "#EFF6FF",
  priBd:   "#BFDBFE",
  green:   "#16A34A",
  greenBg: "#DCFCE7",
  red:     "#DC2626",
  redBg:   "#FEE2E2",
  redBd:   "#FCA5A5",
  amber:   "#D97706",
  amberBg: "#FFFBEB",
  amberBd: "#FDE68A",
};

const DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const uid = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;
const LS_KEY = "kc-team-v1";

// ── Avatar kleur op basis van naam ───────────────────────────────────────────
const AVATAR_KLEUREN = ["#2563EB","#7C3AED","#DB2777","#D97706","#16A34A","#0891B2","#DC2626"];
function avatarKleur(naam) {
  let hash = 0;
  for (let i = 0; i < (naam||"").length; i++) hash = naam.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_KLEUREN[Math.abs(hash) % AVATAR_KLEUREN.length];
}
function Avatar({ naam, size = 36 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: avatarKleur(naam), display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.42, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
      {(naam||"?")[0].toUpperCase()}
    </div>
  );
}

// ── ISO week helpers ──────────────────────────────────────────────────────────
function isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const y = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { year: d.getUTCFullYear(), week: Math.ceil(((d - y) / 86400000 + 1) / 7) };
}
function targetDate(dayIdx, weekOffset = 0) {
  const now = new Date();
  const cur = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const t = new Date(now);
  t.setDate(t.getDate() + (dayIdx - cur + weekOffset * 7));
  return t;
}
function weekDayKeysFor(offset) {
  return DAYS.map((d, i) => {
    const date = targetDate(i, offset);
    const { year, week } = isoWeekOf(date);
    return `${year}-W${String(week).padStart(2, "0")}-${d}`;
  });
}
function getWeekLabel(offset = 0) {
  const now = new Date();
  const cur = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const mon = new Date(now); mon.setDate(now.getDate() - cur + offset * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const M = ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"];
  return `${mon.getDate()} ${M[mon.getMonth()]} – ${sun.getDate()} ${M[sun.getMonth()]}`;
}

// ── Uren berekening ───────────────────────────────────────────────────────────
function berekenDagUren(start, eind, pauze = 0) {
  if (!start || !eind) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = eind.split(":").map(Number);
  let min = eh * 60 + em - (sh * 60 + sm) - (pauze || 0);
  if (min <= 0) return 0;
  return Math.round(min / 15) * 0.25;
}
function weekTotaalUren(medewerker, offset) {
  const keys = new Set(weekDayKeysFor(offset));
  return (medewerker.uren || [])
    .filter(u => keys.has(u.dayKey))
    .reduce((sum, u) => sum + berekenDagUren(u.start, u.eind, u.pauze || 0), 0);
}

// ── Lokale opslag ─────────────────────────────────────────────────────────────
function loadLocal() {
  try { const s = localStorage.getItem(LS_KEY); if (s) return JSON.parse(s); } catch {}
  return null;
}
function saveLocal(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}

// ── Supabase helpers ──────────────────────────────────────────────────────────
async function sbLoad(sb, tenantId) {
  try {
    const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    if (rows?.length && rows[0]?.data) return rows[0].data;
  } catch (e) { console.warn("[Team] load failed:", e); }
  return null;
}
async function sbSave(sb, tenantId, data) {
  try {
    const exists = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
    if (exists?.length) {
      await sb.update("team_data", `tenant_id=eq.${tenantId}`, { data, updated_at: new Date().toISOString() });
    } else {
      await sb.insert("team_data", { tenant_id: tenantId, data, updated_at: new Date().toISOString() });
    }
  } catch (e) { console.warn("[Team] save failed:", e); }
}

function initState() {
  const saved = loadLocal();
  if (saved?.medewerkers) return saved;
  return { medewerkers: [] };
}

// ─────────────────────────────────────────────────────────────────────────────
// TEAM MODULE
// ─────────────────────────────────────────────────────────────────────────────
export default function TeamModule({ sb, tenantId, activeTab, onTabChange }) {
  const [state, setState] = useState(initState);
  const [syncStatus, setSyncStatus] = useState("local");
  const [urenMedewerker, setUrenMedewerker] = useState(null);
  const [urenWeekOffset, setUrenWeekOffset] = useState(0);
  const saveTimerRef = useRef(null);
  const localRef = useRef(false);
  const pollRef = useRef(null);

  const { medewerkers } = state;

  // ── Persistentie ────────────────────────────────────────────────────────────
  const update = useCallback((fn) => setState(s => {
    const next = { ...s, ...fn(s) };
    saveLocal(next);
    if (sb && tenantId) {
      localRef.current = true;
      setSyncStatus("syncing");
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        await sbSave(sb, tenantId, next);
        setSyncStatus("synced");
        setTimeout(() => { localRef.current = false; }, 500);
      }, 600);
    }
    return next;
  }), [sb, tenantId]);

  // ── Supabase laden + polling ────────────────────────────────────────────────
  useEffect(() => {
    if (!sb || !tenantId) return;
    let lastUpdatedAt = null;

    (async () => {
      setSyncStatus("syncing");
      const remote = await sbLoad(sb, tenantId);
      if (remote?.medewerkers) {
        setState(local => {
          const merged = { ...local, ...remote };
          saveLocal(merged);
          return merged;
        });
      }
      setSyncStatus("synced");
    })();

    const poll = async () => {
      if (localRef.current) return;
      try {
        const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
        if (!rows?.length) return;
        const row = rows[0];
        if (lastUpdatedAt && row.updated_at === lastUpdatedAt) return;
        if (!lastUpdatedAt) { lastUpdatedAt = row.updated_at; return; }
        lastUpdatedAt = row.updated_at;
        setState(local => {
          const merged = { ...local, ...row.data };
          saveLocal(merged);
          return merged;
        });
      } catch {}
    };

    pollRef.current = setInterval(poll, 2000);
    poll();

    return () => { clearInterval(pollRef.current); clearTimeout(saveTimerRef.current); };
  }, [sb, tenantId]);

  // ── CRUD medewerkers ─────────────────────────────────────────────────────────
  const voegToe = (naam) => {
    if (!naam.trim()) return;
    update(s => ({ medewerkers: [...s.medewerkers, { id: uid(), naam: naam.trim(), uren: [] }] }));
  };
  const verwijder = (id) => {
    if (!confirm(`Medewerker verwijderen? Alle uren-historie gaat ook verloren.`)) return;
    update(s => ({ medewerkers: s.medewerkers.filter(m => m.id !== id) }));
  };

  // ── Uren bijwerken ───────────────────────────────────────────────────────────
  const updateUren = (medewerkerId, dayKey, partial) => {
    update(s => ({
      medewerkers: s.medewerkers.map(m => {
        if (m.id !== medewerkerId) return m;
        const uren = Array.isArray(m.uren) ? [...m.uren] : [];
        const idx = uren.findIndex(u => u.dayKey === dayKey);
        if (idx >= 0) {
          const updated = { ...uren[idx], ...partial };
          if (!updated.start && !updated.eind) uren.splice(idx, 1);
          else uren[idx] = updated;
        } else {
          const nieuw = { dayKey, start: "", eind: "", pauze: 30, ...partial };
          if (nieuw.start || nieuw.eind) uren.push(nieuw);
        }
        return { ...m, uren };
      })
    }));
  };

  // ── CSV download ─────────────────────────────────────────────────────────────
  const downloadCSV = (offset) => {
    const keys = weekDayKeysFor(offset);
    const { week, year } = isoWeekOf(targetDate(0, offset));
    const rows = [["Medewerker", ...DAYS, "Totaal"]];
    medewerkers.forEach(m => {
      const dagUren = keys.map((dk, i) => {
        const entry = (m.uren || []).find(u => u.dayKey === dk);
        return entry ? berekenDagUren(entry.start, entry.eind, entry.pauze || 0) : 0;
      });
      const totaal = dagUren.reduce((a, b) => a + b, 0);
      rows.push([m.naam, ...dagUren.map(u => u > 0 ? u.toFixed(2).replace(".", ",") : ""), totaal.toFixed(2).replace(".", ",")]);
    });
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `uren-week${String(week).padStart(2,"0")}-${year}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg, fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 13, color: C.txt, overflow: "hidden" }}>

      {/* ── Header ── */}
      <header style={{ background: C.hdr, borderBottom: `1px solid ${C.hdrBdr}`, height: 52, display: "flex", alignItems: "center", padding: "0 18px", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>👥 Team</span>
        <div style={{ flex: 1 }} />
        {sb && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: syncStatus === "syncing" ? "#FCD34D" : "rgba(255,255,255,.45)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: syncStatus === "syncing" ? "#FCD34D" : "#4ADE80" }} />
            {syncStatus === "syncing" ? "synchroniseren…" : "gesynchroniseerd"}
          </div>
        )}
      </header>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20, maxWidth: 600 }}>
        <div style={{ fontSize: 11, color: C.txt3, marginBottom: 16 }}>
          Klik op een naam voor uren-overzicht
        </div>

        {/* Medewerkers lijst */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {medewerkers.length === 0 && (
            <div style={{ padding: "40px 0", textAlign: "center", color: C.txt3 }}>
              <Users size={36} strokeWidth={1} style={{ display: "block", margin: "0 auto 12px" }} />
              <p>Nog geen medewerkers. Voeg ze hieronder toe.</p>
            </div>
          )}
          {medewerkers.map(m => {
            const weekUren = weekTotaalUren(m, 0);
            return (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 10, cursor: "pointer", transition: "box-shadow .12s" }}
                onClick={() => { setUrenMedewerker(m.id); setUrenWeekOffset(0); }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.08)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <Avatar naam={m.naam} size={40} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.txt }}>{m.naam}</div>
                  {weekUren > 0 && (
                    <div style={{ fontSize: 11, color: C.amber, marginTop: 2, fontWeight: 600 }}>
                      {weekUren.toFixed(2).replace(".", ",")}u deze week
                    </div>
                  )}
                </div>
                <button onClick={e => { e.stopPropagation(); verwijder(m.id); }}
                  style={{ background: C.redBg, border: `1px solid ${C.redBd}`, color: C.red, width: 30, height: 30, borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={13} />
                </button>
                <span style={{ color: C.txt3, fontSize: 16 }}>›</span>
              </div>
            );
          })}
        </div>

        {/* Toevoegen */}
        <ToevoegenForm onAdd={voegToe} />
      </div>

      {/* ── Uren modal ── */}
      {urenMedewerker && (
        <UrenModal
          medewerker={medewerkers.find(m => m.id === urenMedewerker)}
          weekOffset={urenWeekOffset}
          setWeekOffset={setUrenWeekOffset}
          onUpdateUren={updateUren}
          onDownload={downloadCSV}
          onClose={() => setUrenMedewerker(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TOEVOEGEN FORM
// ─────────────────────────────────────────────────────────────────────────────
function ToevoegenForm({ onAdd }) {
  const [naam, setNaam] = useState("");
  const submit = () => { if (!naam.trim()) return; onAdd(naam); setNaam(""); };
  return (
    <div style={{ display: "flex", gap: 8, padding: "14px 16px", background: C.bg2, border: `1.5px dashed ${C.bdr2}`, borderRadius: 10 }}>
      <input
        style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 13, color: C.txt, fontFamily: "inherit" }}
        placeholder="+ Medewerker toevoegen…"
        value={naam}
        onChange={e => setNaam(e.target.value)}
        onKeyDown={e => e.key === "Enter" && submit()}
      />
      {naam.trim() && (
        <button onClick={submit} style={{ background: C.pri, border: "none", color: "#fff", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>
          Toevoegen
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UREN MODAL
// ─────────────────────────────────────────────────────────────────────────────
function UrenModal({ medewerker, weekOffset, setWeekOffset, onUpdateUren, onDownload, onClose }) {
  if (!medewerker) return null;

  const keys = weekDayKeysFor(weekOffset);
  const { week, year } = isoWeekOf(targetDate(0, weekOffset));
  const totaal = weekTotaalUren(medewerker, weekOffset);

  // Hoeveel weken hebben data
  const allWeeks = new Set((medewerker.uren || []).map(u => {
    const m = /^(\d{4}-W\d{2})-/.exec(u.dayKey);
    return m ? m[1] : null;
  }).filter(Boolean));

  // Sluit op Escape
  useEffect(() => {
    const h = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const inp = {
    background: C.bg3, border: `1px solid ${C.bdr}`, color: C.txt,
    padding: "5px 6px", fontFamily: "inherit", fontSize: 12,
    outline: "none", borderRadius: 4, width: "100%",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.bg2, borderRadius: 12, width: "100%", maxWidth: 540, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 18px", borderBottom: `1px solid ${C.bdr}` }}>
          <Avatar naam={medewerker.naam} size={36} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.txt }}>{medewerker.naam}</div>
            <div style={{ fontSize: 11, color: C.txt3, marginTop: 1 }}>Uren-registratie</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, display: "flex", padding: 4 }}><X size={18} /></button>
        </div>

        {/* Week navigatie */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px 0", gap: 8 }}>
          <button onClick={() => setWeekOffset(o => o - 1)}
            style={{ background: "transparent", border: `1px solid ${C.bdr}`, color: C.txt2, padding: "6px 14px", fontFamily: "inherit", fontSize: 12, cursor: "pointer", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
            <ChevronLeft size={13} /> vorige
          </button>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.txt }}>Week {week} · {year}</div>
            <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>
              Totaal: <strong style={{ color: C.amber, fontSize: 13 }}>{totaal.toFixed(2).replace(".", ",")}u</strong>
            </div>
          </div>
          <button onClick={() => setWeekOffset(o => o + 1)}
            style={{ background: "transparent", border: `1px solid ${C.bdr}`, color: C.txt2, padding: "6px 14px", fontFamily: "inherit", fontSize: 12, cursor: "pointer", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
            volgende <ChevronRight size={13} />
          </button>
        </div>

        {/* Dag tabel */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 18px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {DAYS.map((d, i) => {
              const dk = keys[i];
              const entry = (medewerker.uren || []).find(u => u.dayKey === dk) || { start: "", eind: "", pauze: 30 };
              const dagUren = berekenDagUren(entry.start, entry.eind, entry.pauze || 0);
              return (
                <div key={dk} style={{ display: "grid", gridTemplateColumns: "36px 1fr 1fr 70px 52px", gap: 6, alignItems: "center", padding: "6px 4px", borderBottom: `1px solid ${C.bdr}`, fontSize: 12 }}>
                  <div style={{ fontWeight: 600, color: C.txt }}>{d}</div>
                  <input type="time" style={inp} value={entry.start || ""} onChange={e => onUpdateUren(medewerker.id, dk, { start: e.target.value })} />
                  <input type="time" style={inp} value={entry.eind || ""} onChange={e => onUpdateUren(medewerker.id, dk, { eind: e.target.value })} />
                  <select style={{ ...inp, cursor: "pointer" }} value={entry.pauze ?? 30} onChange={e => onUpdateUren(medewerker.id, dk, { pauze: parseInt(e.target.value) })}>
                    {[0, 15, 30, 45, 60].map(p => <option key={p} value={p}>{p === 0 ? "geen" : `${p}m`}</option>)}
                  </select>
                  <div style={{ textAlign: "right", fontWeight: 600, color: dagUren > 0 ? C.txt : C.txt3, fontVariantNumeric: "tabular-nums" }}>
                    {dagUren > 0 ? `${dagUren.toFixed(2).replace(".", ",")}u` : "—"}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Geschiedenis */}
          {allWeeks.size > 1 && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.bdr}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                Geschiedenis ({allWeeks.size} weken)
              </div>
              <div style={{ fontSize: 11, color: C.txt3 }}>
                Navigeer met vorige / volgende om eerdere weken te bekijken.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderTop: `1px solid ${C.bdr}`, gap: 8 }}>
          <button onClick={() => onDownload(weekOffset)}
            style={{ background: "transparent", border: `1px solid ${C.amberBd}`, color: C.amber, padding: "8px 14px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer", borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <Download size={13} /> Download weekrapport (CSV)
          </button>
          <button onClick={onClose}
            style={{ background: C.hdr, border: "none", color: "#fff", padding: "8px 20px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer", borderRadius: 6 }}>
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT: gedeelde medewerkers lezen vanuit andere modules (HACCP etc.)
// ─────────────────────────────────────────────────────────────────────────────
export function getMedewerkers() {
  try {
    const s = localStorage.getItem("kc-team-v1");
    if (s) return JSON.parse(s)?.medewerkers || [];
  } catch {}
  return [];
}
